import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { TProjects } from 'store/companies/companiesTypes';
import { red } from 'utils/style/colors';
import { NumberOrNull, UserType, TimeType } from 'store/entity/types';

import Icon from 'components/Icon';
import Modal from 'components/Modal';
import Button from 'components/Button';
import notify from 'components/Notify';
import messages from './Messages';
import TaskSearch from 'components/TaskSearch';
import { display } from 'components/ErrorView/ErrorDialog';
import TaskReminders from 'containers/Tasks/TasksManagement/TaskReminders';
import {
  DeleteTaskWrapper,
  ArchiveTaskWrapper,
  ArchiveDeleteTaskHint,
  ArchiveDeleteTaskContainer,
} from './styles';
import SanitizedHTML from 'react-sanitized-html';
import Select, { IOption } from '../Select/Select';
import { notifyError } from '../Notify/Notify';
import { IReminderInfo, ITask } from 'store/tasks/tasksType';
import { IPermission } from 'store/entity/IPermission';
import i18n from '../../i18n';

interface IComponentProps {
  onCancelDeleteTask: () => void;
  onChangeParentTask: (parentId: NumberOrNull | string) => Promise<any>;
  onChangePreferencesInput: (task: ITask) => Promise<any>;
  onClose: () => void;
  onDeleteTask: (taskId: number, taskName: string) => void;
  onRestoreTask: (taskId: number, taskName: string) => void;
  onDraftTask: (task: ITask) => Promise<void>;
  onArchiveOrRestoring: (
    type: 'archive' | 'restore',
    taskId: number
  ) => Promise<void>;
  onSearchTask: (text: string, projectId: number) => Promise<any>;
  moveToProject: (taskId: number, projectId: number) => Promise<any>;
  parentTitle: string;
  permission: IPermission;
  show: boolean;
  task: ITask;
  projects: TProjects;
}

interface IComponentState {
  task: ITask;
  saving: boolean;
  template?: ITask;
  selectedTab: string;
  updatingParent: boolean;
  isTaskDeleting: boolean;
  isTaskArchiving: boolean;
  isTaskDrafting: boolean;
  selectedProjectToMove?: number;
}

class TaskPreferences extends React.PureComponent<
  IComponentProps,
  IComponentState
> {
  state = {
    selectedTab: 'setParent',
    task: this.props.task,
    updatingParent: false,
    saving: false,
    isTaskDeleting: false,
    isTaskArchiving: false,
    isTaskDrafting: false,
    selectedProjectToMove: undefined,
  };

  static getDerivedStateFromProps(
    nextProps: IComponentProps,
    prevState: IComponentState
  ) {
    if (prevState.task.id !== nextProps.task.id) {
      return { task: nextProps.task };
    }
    if (prevState.task.parentId !== nextProps.task.parentId) {
      return { task: nextProps.task };
    }
    if (prevState.task.archived !== nextProps.task.archived) {
      return { task: nextProps.task };
    }
    return null;
  }

  handleSelectATab = (selectedTab: number | string) => {
    this.setState({ selectedTab: selectedTab as string });
  };

  handleChangeParentTask = (parentId: NumberOrNull | string = null) => {
    this.setState({ updatingParent: true });
    this.props.onChangeParentTask(parentId).then(
      () => {
        this.setState({ updatingParent: false });
        notify(i18n.t('taskUpdated', { ns: 'companyManagement' }));
      },
      (err: any) => {
        this.setState({ updatingParent: false });
        if (err.custom) {
          notify(
            i18n.t('aTaskCannotBeChildOfItself', { ns: 'companyManagement' }),
            'error'
          );
        } else {
          display(err);
        }
      }
    );
  };

  handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const task = cloneDeep(this.state.task);
    const target = event.target;
    const value = target.checked;
    task[target.name as 'lockMark' | 'lockProps'] = value;
    this.setState({ task });
  };

  handleSaveButton = () => {
    this.setState({ saving: true });
    this.props.onChangePreferencesInput(this.state.task).then(
      () => {
        notify(i18n.t('taskUpdated', { ns: 'companyManagement' }));
        this.setState({ saving: false });
      },
      (err: any) => {
        this.setState({ saving: false });
        display(err);
      }
    );
  };

  handleAddReminder = () => {
    const reminder: IReminderInfo = {
      target: 'verifier',
      offsetM: -60,
      timeTarget: 'verifyDate',
      message: '',
    };
    const reminderInfos = [...this.state.task.reminderInfos, reminder];
    this.setState({ task: { ...this.state.task, reminderInfos } });
  };

  removeReminder = (reminderIndex: number) => {
    const reminderInfos = [...this.state.task.reminderInfos];
    reminderInfos.splice(reminderIndex, 1);
    this.setState({ task: { ...this.state.task, reminderInfos } });
  };

  handleReminderInputChange = (
    reminderIndex: number,
    inputType: 'reminder' | 'target' | 'timeTarget' | 'message',
    value: string
  ) => {
    const reminderInfos = cloneDeep(this.state.task.reminderInfos);
    if (inputType === 'reminder') {
      reminderInfos[reminderIndex].offsetM = Number(value);
    } else if (inputType === 'target') {
      reminderInfos[reminderIndex].target = value as UserType;
    } else if (inputType === 'timeTarget') {
      reminderInfos[reminderIndex].timeTarget = value as TimeType;
    }
    this.setState({ task: { ...this.state.task, reminderInfos } });
  };

  handleSave = () => {
    return this.props.onChangePreferencesInput(this.state.task);
  };

  handleDeleteTask =
    (
      taskId: number,
      taskName: string,
      templateName: string,
      subTasksTitle?: string
    ) =>
    () => {
      const taskTitle = `${templateName} ${taskName}`;
      const confirm = window.confirm(
        messages.confirmDeleteATask(taskTitle, subTasksTitle)
      );
      if (confirm) {
        this.setState({ isTaskDeleting: true });
        this.props.onDeleteTask(taskId, taskTitle);
      }
    };

  handleRestoreTask =
    (
      taskId: number,
      taskName: string,
      templateName: string,
      subTasksTitle?: string
    ) =>
    () => {
      const taskTitle = `${templateName} ${taskName}`;
      const confirm = window.confirm(
        messages.confirmRestoreTask(taskTitle, subTasksTitle)
      );
      if (confirm) {
        this.setState({ isTaskDeleting: true });
        this.props.onRestoreTask(taskId, taskTitle);
      }
    };

  onCancelDeleteTask = () => {
    this.setState({ isTaskDeleting: false });
    this.props.onCancelDeleteTask();
  };

  handleArchiveOrRestore = (type: 'archive' | 'restore') => () => {
    this.setState({ isTaskArchiving: true });
    const { task, onArchiveOrRestoring } = this.props;
    onArchiveOrRestoring(type, task.id)
      .then(() => {
        this.setState({ isTaskArchiving: false });
      })
      .catch((err) => {
        display(err);
      });
  };

  handleDraftTask = () => () => {
    this.setState({ isTaskDrafting: true });
    const { task, onDraftTask } = this.props;
    onDraftTask(task)
      .then(() => {
        this.setState({
          isTaskDrafting: false,
          task: { ...task, draft: true },
        });
      })
      .catch((err) => {
        this.setState({
          isTaskDrafting: false,
        });
        display(err);
      });
  };

  render() {
    const { show, onClose, onSearchTask, projects } = this.props;
    const {
      task,
      saving,
      selectedTab,
      updatingParent,
      isTaskDeleting,
      isTaskArchiving,
      isTaskDrafting,
      selectedProjectToMove,
    } = this.state;
    const modalTabs = [
      {
        id: 'setParent',
        title: i18n.t('setParentTab', { ns: 'companyManagement' }),
      },
      // { id: 'deleteArchiveTask', title: messages.deleteArchiveTask },
    ];

    // if (this.props.isSupportOrOwner) {
    //   modalTabs = [
    //     // { id: 'setReminder', title: messages.setReminderTab },
    //     { id: 'setParent', title: messages.setParentTab },
    //     // { id: 'lock', title: messages.lockTheTask },
    //     // { id: 'deleteArchiveTask', title: messages.deleteArchiveTask },
    //   ];
    // }

    const availableMoveProjects = Object.values(projects || {}).reduce(
      (current: IOption[], project) => {
        if (
          project.templates.find(
            (it: any) =>
              it.taskId === task.templateId &&
              it.allowRoot &&
              it.id !== task.projectId
          )
        )
          current.push({ label: project.name, value: project.id });
        return current;
      },
      [] as IOption[]
    );
    return (
      <Modal
        title={i18n.t('preferences', { ns: 'companyManagement' })}
        onClose={onClose}
        isActive={show}
        tabs={modalTabs}
        onSelectATab={this.handleSelectATab}
        selectedTab={selectedTab}
      >
        {selectedTab === 'setReminder' && (
          <TaskReminders
            reminders={task.reminderInfos}
            dueDateTitle={task.dueDateTitle}
            onSaveButton={this.handleSave}
            onAddReminder={this.handleAddReminder}
            assigneeTitle={task.assigneeTitle}
            removeReminder={this.removeReminder}
            followersTitle={task.followersTitle}
            onReminderChange={this.handleReminderInputChange}
          />
        )}

        {selectedTab === 'setParent' &&
          (updatingParent ? (
            <div className="progress">
              <div className="indeterminate" />
            </div>
          ) : task.parentId ? (
            <div style={{ display: 'flex' }}>
              <span style={{ padding: '3px 10px' }}>
                {i18n.t('currentStatus', { ns: 'companyManagement' }) as string}
                <b>
                  <SanitizedHTML
                    style={{ display: 'inline' }}
                    allowedAttributes={{ span: ['style'] }}
                    allowedTags={['span']}
                    html={messages.statusTitle(this.props.parentTitle)}
                  />
                </b>
              </span>
              <a
                href="#!"
                onClick={(e) => {
                  e.preventDefault();
                  this.handleChangeParentTask(null);
                }}
              >
                <Icon type="trash-2" />
              </a>
            </div>
          ) : (
            <div style={{ paddingBottom: '1.2em' }}>
              <p>
                {
                  i18n.t('searchATaskToSelectItAsTheParentOfThisTask', {
                    ns: 'companyManagement',
                  }) as string
                }
              </p>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <TaskSearch
                  onSearch={(text: string) =>
                    onSearchTask(text, task.projectId)
                  }
                  onSelect={this.handleChangeParentTask}
                  placeholder={
                    i18n.t('searchTask', { ns: 'companyManagement' }) as string
                  }
                />
              </div>
              <div style={{ marginTop: '20px' }}>
                <p>
                  {
                    i18n.t('selectAProjectToMoveTaskTo', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </p>
                <Select
                  options={availableMoveProjects}
                  values={
                    selectedProjectToMove
                      ? ([
                          availableMoveProjects.find(
                            (it: IOption) => it.value === selectedProjectToMove
                          ),
                        ] as IOption[])
                      : ([] as IOption[])
                  }
                  onChangeValues={(values) => {
                    this.setState({
                      selectedProjectToMove:
                        Number(values[0]?.value) || undefined,
                    });
                  }}
                  placeholder={
                    i18n.t('project', { ns: 'companyManagement' }) as string
                  }
                />
                <Button
                  disabled={!selectedProjectToMove}
                  onClick={() => {
                    this.setState({ updatingParent: true });
                    this.props
                      .moveToProject(task.id, selectedProjectToMove!)
                      .then(
                        () => {
                          this.setState({ updatingParent: false });
                        },
                        (err) => {
                          notifyError(err);
                          this.setState({ updatingParent: false });
                        }
                      );
                  }}
                >
                  {
                    i18n.t('moveToProject', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </Button>
              </div>
            </div>
          ))}

        {selectedTab === 'lock' && (
          <React.Fragment>
            <p>
              <input
                type="checkbox"
                id="lockProps"
                name="lockProps"
                checked={task.lockProps}
                disabled={
                  this.props.task.lockProps && !this.props.permission.unlock
                }
                onChange={this.handleInputChange}
              />
              <label htmlFor="lockProps">
                {i18n.t('lockProps', { ns: 'companyManagement' }) as string}
              </label>
            </p>
            <p>
              <input
                type="checkbox"
                id="lockMark"
                name="lockMark"
                checked={task.lockMark}
                onChange={this.handleInputChange}
              />
              <label htmlFor="lockMark">
                {i18n.t('lockMark', { ns: 'companyManagement' }) as string}
              </label>
            </p>
            {(task.lockProps !== this.props.task.lockProps ||
              task.lockMark !== this.props.task.lockMark) && (
              <React.Fragment>
                <p style={{ color: red }}>
                  {i18n.t('taskChanged', { ns: 'companyManagement' }) as string}
                </p>
                <Button
                  color="green"
                  onClick={this.handleSaveButton}
                  loading={saving}
                >
                  {i18n.t('save', { ns: 'companyManagement' }) as string}
                </Button>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        {selectedTab === 'deleteArchiveTask' && (
          <ArchiveDeleteTaskContainer>
            {!task.deleteState && (
              <>
                <ArchiveTaskWrapper>
                  <ArchiveDeleteTaskHint>
                    <Icon className="right" type="info" />
                    {
                      i18n.t('archiveTaskHint', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </ArchiveDeleteTaskHint>
                  <Button
                    color={task.archived ? 'green' : 'blue'}
                    loading={isTaskArchiving}
                    onClick={this.handleArchiveOrRestore(
                      task.archived ? 'restore' : 'archive'
                    )}
                  >
                    {task.archived ? (
                      <>
                        <Icon className="right" type="upload" />
                        {i18n.t('unArchiveTask', { ns: 'companyManagement' })}
                      </>
                    ) : (
                      <>
                        <Icon className="right" type="archive" />
                        {i18n.t('archiveTask', { ns: 'companyManagement' })}
                      </>
                    )}
                  </Button>
                </ArchiveTaskWrapper>
                <DeleteTaskWrapper>
                  <ArchiveDeleteTaskHint>
                    <Icon className="right" type="info" />
                    {messages.hintToDraftTask(
                      task.taskName,
                      task.textTitle,
                      task.subTasksTitle
                        ? task.subTasksTitle
                        : i18n.t('step', { ns: 'companyManagement' })
                    )}
                  </ArchiveDeleteTaskHint>
                  {!task.draft ? (
                    <Button
                      color="green"
                      onClick={this.handleDraftTask()}
                      loading={isTaskDrafting}
                    >
                      <Icon className="right" type="trash-2" />
                      {
                        i18n.t('draftTask', {
                          ns: 'companyManagement',
                        }) as string
                      }
                    </Button>
                  ) : (
                    <p> {messages.taskIsDraft(task.taskName)}</p>
                  )}
                </DeleteTaskWrapper>
              </>
            )}
            <DeleteTaskWrapper>
              <ArchiveDeleteTaskHint>
                <Icon className="right" type="info" />
                {messages.hintToDeleteTask(
                  task.taskName,
                  task.textTitle,
                  task.subTasksTitle
                    ? task.subTasksTitle
                    : i18n.t('step', { ns: 'companyManagement' }),
                  task.deleteState
                )}
              </ArchiveDeleteTaskHint>
              {!isTaskDeleting && (
                <Button
                  color="red"
                  disabled={task.deleteState === 'ParentDeleted'}
                  onClick={
                    task.deleteState
                      ? this.handleRestoreTask(
                          task.id,
                          task.textTitle,
                          task.taskName,
                          task.subTasksTitle
                        )
                      : this.handleDeleteTask(
                          task.id,
                          task.textTitle,
                          task.taskName,
                          task.subTasksTitle
                        )
                  }
                >
                  <Icon
                    className="right"
                    type={task.deleteState ? 'rotate-ccw' : 'trash-2'}
                  />
                  {task.deleteState
                    ? (i18n.t('restoreTask', {
                        ns: 'companyManagement',
                      }) as string)
                    : (i18n.t('deleteTask', {
                        ns: 'companyManagement',
                      }) as string)}
                </Button>
              )}
            </DeleteTaskWrapper>
          </ArchiveDeleteTaskContainer>
        )}
      </Modal>
    );
  }
}

export default TaskPreferences;
